// @flow
import moment from 'moment'
import { OptionPricingFormula, ESBAmericanType } from '../greeks'
import global_config from '../../utils/constants'
import type {
  VolatilityCalcData, 
  Greeks, 
  GreeksCalcData, 
  CallResult, 
  PutResult 
} from '../../types/greeks'


// --- Implied Volatility ---
export const calcImpliedVolatility = (payload: VolatilityCalcData): VolatilityCalcData => {

  // form values
  const {
    optionType,
    assetPrice,
    strikePrice,
    expireDay,
    riskFree,
    dividendYield,
    marketPrice
  }: VolatilityCalcData = payload

  // determine option type
  const isCall: boolean = optionType === 'call option'

  // calculate implied volatility
  const pricingFormula: Function = new OptionPricingFormula()
  const impliedVolatility: number = pricingFormula.gImpliedVolatilityNR(isCall, Number(assetPrice), Number(strikePrice), Number(expireDay) / 365, Number(riskFree) / 100, Number(dividendYield) / 100, Number(marketPrice))
  const calculatePercent: number = isNaN(impliedVolatility) ? 0 : impliedVolatility * 100

  // return form data with implied volatility
  const formData: VolatilityCalcData = { ...payload }
  formData.volatility = calculatePercent.toFixed(3) + '%'
  return formData
}

// --- Greeks ---
export const calcGreeks = (payload: GreeksCalcData): Greeks => {

  // form data
  const {
    assetPrice,
    strikePrice,
    expireDay,
    riskFree,
    dividendYield,
    volatility,
    ds,
    rounding
  }: GreeksCalcData = payload

  // greeks.js
  const calcRiskFree: number = Number(riskFree) / 100
  const calcDividendYield: number = Number(dividendYield) / 100
  const calcDs: number = Number(ds) / 100
  const calcVolatility: number = Number(volatility) / 100
  const calcExpireDay: number = Number(expireDay) / 365
  const calcRounding: number = Number(rounding) !== null ? Number(rounding) : 5

  const pricingFormula: Function = new OptionPricingFormula()
  const call: CallResult = {
    optionValue: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.OptionValue, true, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding),
    delta: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.Delta, true, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding),
    gamma: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.Gamma, true, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding),
    theta: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.Theta, true, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding),
    vega: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.Vega, true, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding)
  }

  const put: PutResult = {
    optionValue: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.OptionValue, false, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding),
    delta: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.Delta, false, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding),
    gamma: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.Gamma, false, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding),
    theta: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.Theta, false, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding),
    vega: pricingFormula.eBSAmericanApprox2002(ESBAmericanType.Vega, false, Number(assetPrice), Number(strikePrice), calcExpireDay, calcRiskFree, calcDividendYield, calcVolatility, calcDs).toFixed(calcRounding)
  }

  // resolve to update Greeks redux
  const greeks: Greeks = {
    formData: { ...payload },
    callResult: {
      optionValue: isNaN(call.optionValue) ? 0 : call.optionValue,
      delta: isNaN(call.delta) ? 0 : call.delta,
      gamma: isNaN(call.gamma) ? 0 : call.gamma,
      theta: isNaN(call.theta) ? 0 : call.theta,
      vega: isNaN(call.vega) ? 0 : call.vega
    },
    putResult: {
      optionValue: isNaN(put.optionValue) ? 0 : put.optionValue,
      delta: isNaN(put.delta) ? 0 : put.delta,
      gamma: isNaN(put.gamma) ? 0 : put.gamma,
      theta: isNaN(put.theta) ? 0 : put.theta,
      vega: isNaN(put.vega) ? 0 : put.vega
    }
  }

  return greeks
}

// --- Trades & Transactions ---
// filter out closed trades
export const filterClosed = (listOfTrades: Object[]): Promise<Object[]> => {
  return new Promise(resolve => resolve(listOfTrades.filter(x => !x.closedDate)))
}

// get first trade
export const getFirstTrade = (listOfTrades: Object[]): Promise<Object> => {
  return new Promise(resolve => resolve(listOfTrades[2]))
}

// add calculated days till expiration to each object
export const addExpireCount = (trade: Object): Promise<Object> => {
  return new Promise(resolve => {
    const { transactions } = trade
    transactions.forEach(x => { x.expireDayCount = calcDaysToExpire(x.expiration) })
    resolve(trade)
  })
}

// calculate number of days till expiration
const calcDaysToExpire = (expiration: string): number => {
  return moment(expiration).diff(moment().startOf('day'), 'days')
}


// get first transaction
export const getFirstTransaction = ({ transactions }: Object): Promise<Object> => {
  return new Promise(resolve => resolve(transactions[0]))
}

// Get Transaction IV
export const impliedVolatility = (transaction: Object): Object => {

  const {
    assetType,
    price,
    expireDayCount,
    strike,
    mark
  }: Object = transaction

  const isCall: string = assetType === 'Call' ? 'call option' : 'put option'

  const iv: Object = {
    optionType: isCall,
    assetPrice: mark || 100,
    strikePrice: strike,
    expireDay: expireDayCount,
    riskFree: global_config.riskFree,
    dividendYield: global_config.dividendYield,
    marketPrice: price,
  }

  return calcImpliedVolatility(iv)
}

export const impliedVolatilityRiskProfile = (transaction: Object, assetPrice): Object => {

  const {
    assetType,
    expireDayCount,
    strike,
    marketPrice,
  }: Object = transaction

  const isCall: string = assetType === 'Call' ? 'call option' : 'put option'

  const iv: Object = {
    optionType: isCall,
    assetPrice: assetPrice || 100,
    strikePrice: strike,
    expireDay: expireDayCount,
    riskFree: global_config.riskFree,
    dividendYield: global_config.dividendYield,
    marketPrice: marketPrice,
  }

  return calcImpliedVolatility(iv)
}

export const xAxisMinMax = (transactions: Object[], diff): number[] => {

  // get all strikes
  const allStrikes = transactions.map(x => x.strike)

  // xMin and xMax
  let xMin: number
  let xMax: number

  allStrikes.forEach((x: number, i: number) => {
    const num: number = x * 0.20
    const min: number = Math.floor(x - num)
    const max: number = Math.ceil(x + num)
    if (i === 0) {
      xMin = min
      xMax = max
    } else {
      xMin = min < xMin ? min : xMin
      xMax = max > xMax ? max : xMax
    }
  })
  
  const differance = (xMax - xMin) / 300

  return calcMinMaxPoints({ xMin, xMax, diff: (diff || differance) })
}

// calculate all points between xMin and xMax
const calcMinMaxPoints = ({ xMin, xMax, diff }: Object): number[] => {
  let allInt: number[] = []
  for (let i = xMin; i <= xMax; i += diff) allInt = [...allInt, Number(i.toFixed(2))]
  return allInt
}

// calculate entry cost
export const calcEntryCost = ({ price, quantity }: Object, contractMultiplier: number = 100): number => {
  return ((price * contractMultiplier) * quantity)
}

// get all options prices
export const optionPrices = ({ transaction, iv, xAxis, entryCost, index }: Object): Promise<Object> => {
  return new Promise(resolve => {

    // greeks data
    const calcData: Object = { ...iv }
    calcData.ds = 0
    calcData.rounding = 3
    calcData.volatility = Number(iv.volatility.replace('%', ''))

    // for each xAxis point: Get all Greeks
    let optionPrices: Object[] = []

    xAxis.forEach((x: number) => {
      calcData.assetPrice = x
      optionPrices = [...optionPrices, calcGreeks(calcData)]
    })

    return resolve({ optionPrices, transaction, entryCost, index })
  })
}

// get all options prices
export const optionPricesWithoutPromise = ({ transaction, iv, xAxis, entryCost, index }) => {
    
    // greeks data
    const calcData: Object = { ...iv }
    calcData.ds = 0
    calcData.rounding = 3
    calcData.volatility = Number(iv.volatility.replace('%', ''))
    // for each xAxis point: Get all Greeks
    let optionPrices: Object[] = []
    let assetP = null;
    
    xAxis.forEach((x: number) => {
      calcData.assetPrice = x;
      optionPrices = [...optionPrices, calcGreeks(calcData)]
    });
    
    return { optionPrices, transaction, entryCost, index }
}



// WEBPACK FOOTER //
// ./src/services/risk/index.js